import React, { useCallback, useMemo } from 'react';
import { observer } from 'mobx-react-lite';
import { freeBetStore, isMobile, parseNumberByPrecision } from '@pascal/pascal-core';
import { userInfoStore } from '@pascal/pascal-core/shared/core/store';
import { doBetStore } from '../../store/doBetStore';
import { winningAmountStore } from '../../store/winningAmountStore';
import { gameStore } from '../../store/gameStore';
import { MARKET_TYPES } from '../../enums/betTypes';
import { defineChipColor } from '../../helpers/chipColor';
import { gameLiveConnection } from '../../services';
import { sideBetsInfoStore } from '../../store/sideBetsInfoStore';
import { cardsStore } from '../../store/cardsStore';
import { betResponseCodeByName } from '../../enums/betResponseCode';
import { roundActiveHandStore } from '../../store/roundActiveHandStore';

export const BetActionsPanel = observer(() => {
  const {
    selectedChip,
    lastBetModel,
    quickBets,
    updateBetsModel,
    isFirstBetInRound,
    setIsFirstBetInRound,
    handleNotificationCode,
  } = doBetStore;
  const { isBetsClosed } = gameStore;
  const { firstHandInfo } = roundActiveHandStore;
  const { isShuffle } = cardsStore;
  const { sideBetsMultiplayer } = sideBetsInfoStore;
  const { activeFreeBet } = freeBetStore;
  const { setLastWin } = winningAmountStore;

  const precision = Number(userInfoStore.precision);
  const handleDoChip = useCallback(
    (type) => {
      if (
        activeFreeBet.id &&
        lastBetModel.betModel &&
        activeFreeBet.remainingCount - lastBetModel.betModel.length === 0
      ) {
        handleNotificationCode(betResponseCodeByName.NotFreeChip);
        return;
      }
      setLastWin(0);
      updateBetsModel(
        {
          type,
          amount: selectedChip,
        },
        () => {
          if (!isFirstBetInRound) {
            setIsFirstBetInRound(true);
            gameLiveConnection.handleDoChip();
          }
        }
      );
    },
    [
      activeFreeBet,
      lastBetModel,
      setLastWin,
      isFirstBetInRound,
      updateBetsModel,
      selectedChip,
      setIsFirstBetInRound,
      handleNotificationCode,
    ]
  );

  const betInfoByTypes = useMemo(() => {
    if (lastBetModel.betModel) {
      return lastBetModel.betModel.reduce((acc, cur) => {
        acc[cur?.type] = cur;
        return acc;
      }, {});
    }
    return null;
  }, [lastBetModel]);

  const baseClass = isMobile() ? 'm-' : '';

  const getFormattedAmount = (type) => {
    const info = betInfoByTypes?.[type];
    if (!info) return null;
    const value = info.hasOwnProperty('winAmount') ? info.winAmount : info.amount;
    if (value == null || value === 0) {
      return '';
    }
    return parseNumberByPrecision(value, 1, precision);
  };

  const getFormattedMultiplier = (odd) => {
    if (odd && odd - 1 > 0) {
      return `${parseNumberByPrecision(odd - 1, 1, precision)}x`;
    }
    return '';
  };

  return (
    <div
      className={`${baseClass}chips-container ${isBetsClosed ? 'disabled' : 'active'} ${
        isShuffle ? 'disabled' : ''
      }`}
    >
      {/* Side 21+3 Bet */}
      <div
        className={`${baseClass}chip is-21 ${defineChipColor(
          (betInfoByTypes?.[MARKET_TYPES.side21plus3]?.hasOwnProperty('winAmount')
            ? betInfoByTypes?.[MARKET_TYPES.side21plus3]?.winAmount
            : betInfoByTypes?.[MARKET_TYPES.side21plus3]?.amount) || null,
          quickBets
        )} ${
          !!betInfoByTypes?.[MARKET_TYPES.side21plus3]?.amount &&
          activeFreeBet.id &&
          getFormattedAmount(MARKET_TYPES.side21plus3)
            ? 'free-chip'
            : ''
        } ${sideBetsMultiplayer?.side21plus3Odd ? 'show-multiplier gold' : ''} ${
          betInfoByTypes?.[MARKET_TYPES.main]?.amount &&
          !betInfoByTypes?.[MARKET_TYPES.side21plus3]?.amount &&
          !isBetsClosed
            ? 'have-pulsation'
            : ''
        } ${betInfoByTypes?.[MARKET_TYPES.side21plus3]?.winAmount ? 'active' : ''}`}
        onClick={() => handleDoChip(MARKET_TYPES.side21plus3)}
      >
        <svg xmlns='http://www.w3.org/2000/svg' className={`${baseClass}chip-bg`}>
          <g className='a'>
            <circle className='c' cx='50%' cy='50%' r='48%' />
          </g>
        </svg>
        <div className={`${baseClass}chip-number`}>
          <div className={`${baseClass}chip-number-text`}>
            {getFormattedAmount(MARKET_TYPES.side21plus3)}
          </div>
          <span className={`${baseClass}multiplier-txt`}>
            {getFormattedMultiplier(sideBetsMultiplayer?.side21plus3Odd)}
          </span>
        </div>
      </div>

      {/* Main Bet */}
      <div
        className={`${baseClass}chip center ${
          betInfoByTypes?.[MARKET_TYPES.main]?.amount || isBetsClosed ? '' : 'have-pulsation'
        } ${defineChipColor(
          (betInfoByTypes?.[MARKET_TYPES.main]?.hasOwnProperty('winAmount')
            ? betInfoByTypes?.[MARKET_TYPES.main]?.winAmount
            : betInfoByTypes?.[MARKET_TYPES.main]?.amount) || null,
          quickBets
        )} ${
          !!betInfoByTypes?.[MARKET_TYPES.main]?.amount &&
          activeFreeBet.id &&
          getFormattedAmount(MARKET_TYPES.main)
            ? 'free-chip'
            : ''
        } ${
          betInfoByTypes?.[MARKET_TYPES.main]?.winAmount || firstHandInfo.isSurrender
            ? 'active'
            : ''
        }`}
        onClick={() => handleDoChip(MARKET_TYPES.main)}
      >
        <svg xmlns='http://www.w3.org/2000/svg' className={`${baseClass}chip-bg`}>
          <g className='a'>
            <circle className='c' cx='50%' cy='50%' r='48%' />
          </g>
        </svg>
        <div className={`${baseClass}chip-number`}>
          <div className={`${baseClass}chip-number-text`}>
            {getFormattedAmount(MARKET_TYPES.main)}
          </div>
        </div>
      </div>

      {/* Perfect Pair Bet */}
      <div
        className={`${baseClass}chip is-pp ${defineChipColor(
          (betInfoByTypes?.[MARKET_TYPES.sidePerfectPair]?.hasOwnProperty('winAmount')
            ? betInfoByTypes?.[MARKET_TYPES.sidePerfectPair]?.winAmount
            : betInfoByTypes?.[MARKET_TYPES.sidePerfectPair]?.amount) || null,
          quickBets
        )} ${
          !!betInfoByTypes?.[MARKET_TYPES.sidePerfectPair]?.amount &&
          activeFreeBet.id &&
          getFormattedAmount(MARKET_TYPES.sidePerfectPair)
            ? 'free-chip'
            : ''
        }  ${sideBetsMultiplayer?.sidePerfectPairOdd ? 'show-multiplier gold' : ''} ${
          betInfoByTypes?.[MARKET_TYPES.main]?.amount &&
          !betInfoByTypes?.[MARKET_TYPES.sidePerfectPair]?.amount &&
          !isBetsClosed
            ? 'have-pulsation'
            : ''
        } ${betInfoByTypes?.[MARKET_TYPES.sidePerfectPair]?.winAmount ? 'active' : ''}`}
        onClick={() => handleDoChip(MARKET_TYPES.sidePerfectPair)}
      >
        <svg xmlns='http://www.w3.org/2000/svg' className={`${baseClass}chip-bg`}>
          <g className='a'>
            <circle className='c' cx='50%' cy='50%' r='48%' />
          </g>
        </svg>
        <div className={`${baseClass}chip-number`}>
          <div className={`${baseClass}chip-number-text`}>
            {getFormattedAmount(MARKET_TYPES.sidePerfectPair)}
          </div>
          <span className={`${baseClass}multiplier-txt`}>
            {getFormattedMultiplier(sideBetsMultiplayer?.sidePerfectPairOdd)}
          </span>
        </div>
      </div>
    </div>
  );
});
